import React from 'react';
import PropTypes from 'prop-types';
import { getToolDisplay } from '../utils/toolDetection';
import './PipelineVisualization.css';

/**
 * PipelineVisualization Component
 * Displays a graphical visualization of a GitLab CI/CD pipeline
 * Shows stages horizontally with jobs and their statuses
 */
const PipelineVisualization = ({ 
  pipelineConfig, 
  pipelineJobs, 
  loadingPipelineConfig, 
  onJobClick 
}) => {
  // Detect dark mode
  const isDarkMode = document.body.classList.contains('dark-mode');
  
  // Show loading state
  if (loadingPipelineConfig) {
    return (
      <div className={`pipeline-visualization-loading ${isDarkMode ? 'dark-mode' : ''}`}>
        Loading pipeline configuration...
      </div>
    );
  }

  // Don't render if no config or no stages
  if (!pipelineConfig || !pipelineConfig.stages || pipelineConfig.stages.length === 0) {
    return null;
  }

  // Merge stages from config with stages from executed jobs to ensure we don't miss any
  const allStagesSet = new Set(pipelineConfig.stages);
  pipelineJobs.forEach(job => {
    if (job.stage) {
      allStagesSet.add(job.stage);
    }
  });
  const allStages = Array.from(allStagesSet);

  return (
    <div className={`pipeline-visualization-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="pipeline-viz-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h4 className="pipeline-viz-title">Pipeline Stages</h4>
        {pipelineConfig.detectedTools && pipelineConfig.detectedTools.length > 0 && (
          <div className="pipeline-detected-tools" style={{ display: 'flex', gap: '8px', fontSize: '12px', alignItems: 'center' }}>
            <span style={{ opacity: 0.7 }}>Runtime:</span>
            {pipelineConfig.detectedTools.map((tool, idx) => {
              const display = getToolDisplay(tool, 'language');
              return (
                <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }} title={display.name}>
                  <span>{display.icon}</span>
                  <span>{display.name}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>
      <div className="pipeline-stages-horizontal">
        {allStages.map((stage, stageIdx) => {
          // Get jobs for this stage from the config
          const stageConfigJobs = pipelineConfig.jobs.filter(job => job.stage === stage);
          
          // Also get jobs from executed pipeline jobs that might not be in config
          const executedJobsInStage = pipelineJobs.filter(pj => pj.stage === stage);
          
          // Merge both: prefer config jobs but add any executed jobs not in config
          const allJobsInStage = [...stageConfigJobs];
          executedJobsInStage.forEach(execJob => {
            if (!allJobsInStage.find(j => j.name === execJob.name)) {
              allJobsInStage.push({ name: execJob.name, stage: execJob.stage });
            }
          });
          
          // Match with actual running jobs to get status
          const stageJobsWithStatus = allJobsInStage.map(configJob => {
            const runningJob = pipelineJobs.find(pj => pj.name === configJob.name);
            return {
              ...configJob,
              status: runningJob?.status || 'not-executed',
              id: runningJob?.id,
              executed: !!runningJob
            };
          });

          return (
            <React.Fragment key={stageIdx}>
              <div className="pipeline-stage-box">
                <div className="stage-box-header">
                  <h5>{stage}</h5>
                </div>
                <div className="stage-box-jobs">
                  {stageJobsWithStatus.length === 0 ? (
                    <div className="stage-no-jobs">No jobs</div>
                  ) : (
                    stageJobsWithStatus.map((job, jobIdx) => {
                      const statusEmoji = {
                        'success': '✅',
                        'failed': '❌',
                        'running': '🔄',
                        'pending': '⏳',
                        'canceled': '🚫',
                        'skipped': '⏭️',
                        'not-executed': '⚪'
                      }[job.status] || '❓';

                      const statusClass = job.status ? `status-${job.status}` : '';

                      return (
                        <div 
                          key={jobIdx}
                          className={`stage-job-box ${statusClass} ${job.id ? 'clickable' : 'not-executed'}`}
                          onClick={() => job.id && onJobClick && onJobClick(pipelineJobs.find(pj => pj.id === job.id))}
                          title={job.executed ? `${job.name} - ${job.status}` : `${job.name} - Not executed in this pipeline`}
                        >
                          <span className="job-status-emoji">{statusEmoji}</span>
                          <span className="job-name-viz">{job.name}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              {stageIdx < allStages.length - 1 && (
                <div className="stage-arrow">→</div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

PipelineVisualization.propTypes = {
  pipelineConfig: PropTypes.shape({
    stages: PropTypes.arrayOf(PropTypes.string),
    jobs: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      stage: PropTypes.string.isRequired,
      script: PropTypes.array
    })),
    detectedTools: PropTypes.arrayOf(PropTypes.string)
  }),
  pipelineJobs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.string,
    stage: PropTypes.string
  })),
  loadingPipelineConfig: PropTypes.bool,
  onJobClick: PropTypes.func
};

PipelineVisualization.defaultProps = {
  pipelineConfig: null,
  pipelineJobs: [],
  loadingPipelineConfig: false,
  onJobClick: null
};

export default PipelineVisualization;
